import { createSignal, type Accessor } from "solid-js";

export type AsyncStatus = "idle" | "loading" | "ready" | "refreshing" | "error";

export interface AsyncPanelState<T> {
  status: AsyncStatus;
  data: T | null;
  error: unknown;
  updatedAt: number | null;
}

export interface AsyncPanel<T> {
  state: Accessor<AsyncPanelState<T>>;
  refresh: () => Promise<T | null>;
  replace: (data: T) => void;
}

export function createAsyncPanel<T>(loader: () => Promise<T>, initial: T | null = null): AsyncPanel<T> {
  const [state, setState] = createSignal<AsyncPanelState<T>>({
    status: initial === null ? "idle" : "ready",
    data: initial,
    error: null,
    updatedAt: initial === null ? null : Date.now(),
  });
  let generation = 0;

  async function refresh(): Promise<T | null> {
    const current = state();
    const requestGeneration = ++generation;
    setState({
      ...current,
      status: current.data === null ? "loading" : "refreshing",
      error: null,
    });
    try {
      const data = await loader();
      if (generation !== requestGeneration) return null;
      setState({ status: "ready", data, error: null, updatedAt: Date.now() });
      return data;
    } catch (error) {
      if (generation !== requestGeneration) return null;
      setState({
        status: "error",
        data: current.data,
        error,
        updatedAt: current.updatedAt,
      });
      return null;
    }
  }

  function replace(data: T): void {
    generation += 1;
    setState({ status: "ready", data, error: null, updatedAt: Date.now() });
  }

  return { state, refresh, replace };
}
